import { config } from '../../config';
import ConsentApi from '../api/ConsentApi';
import { getUserConsentStatus } from '../features/queries/getUserConsentStatus';
import { useM, useQ } from '../lib/query';
import { useLogger } from '../providers/LoggerProvider';
import { __, toastError } from '../utils';
import { useAuth } from '@presov.sk/presov-auth-react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Button,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import InfoIcon from '@mui/icons-material/InfoOutline';
import React from 'react';

const ConsentModal: React.FC = () => {
  const { logger } = useLogger();
  const { authState, logout } = useAuth();

  const [open, setOpen] = React.useState(false);

  const { data } = useQ(
    getUserConsentStatus,
    { id: config.CONSENTS.id },
    { enabled: authState === 'authorized' && !!config.CONSENTS.id },
  );

  React.useEffect(() => {
    if (data && data.status !== 'granted') {
      setOpen(true);
    }
  }, [data]);

  const saveConsent = useM(
    (state: 'granted' | 'denied') => {
      if (!data) {
        return;
      }

      return ConsentApi.saveUserConsent(
        data.consent_id,
        {},
        {
          status: state,
          version: data.version,
        },
      );
    },
    {
      onSuccess: async (_, state) => {
        setOpen(false);
        if (state === 'denied') {
          logout();
        }
      },
      onError: (e, variables) => {
        logger.error('Failed to save consent', {
          action: 'saveConsent',
          error: e,
          data: variables,
        });
        toastError(__('Nepodarilo sa upraviť stav súhlasu'));
      },
    },
  );

  return (
    <Dialog
      open={open}
      maxWidth={'xs'}
      fullWidth
      sx={{
        zIndex: 99999999999999,
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
        }}
      >
        {data?.consent_title}
      </DialogTitle>

      <DialogContent>
        <Stack gap={1}>
          {data?.status === 'outdated' && (
            <Alert severity='info'>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {__('Súhlas bol aktualizovaný')}
              </Typography>
            </Alert>
          )}
          <DialogContentText>{data?.consent_text}</DialogContentText>

          <Stack direction={'row'} alignItems={'center'} gap={0.5}>
            <InfoIcon
              sx={{
                fontSize: 15,
                color: (theme) => theme.palette.info.main,
              }}
            />
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
              {__('Pri nesúhlase budete odhlásený')}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
        }}
      >
        <Button
          onClick={async () => {
            await saveConsent.mutateAsync('denied');
          }}
          variant='outlined'
          disabled={saveConsent.isPending}
        >
          {__('Nesúhlasím')}
        </Button>
        <Button
          onClick={async () => {
            await saveConsent.mutateAsync('granted');
          }}
          variant='contained'
          disabled={saveConsent.isPending}
        >
          {__('Súhlasím')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default ConsentModal;
